import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import axios from 'axios';
import '../../styles/pages.css';
import '../../styles/emailCheck.css';

type CallbackStatus = 'processing' | 'success' | 'error';

const API_BASE    = process.env.REACT_APP_COIPortal_Url;
const FarmersLogo = `${process.env.PUBLIC_URL}/Farmers_Insurance_Group_logo.svg`;

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    NCToken: localStorage.getItem('NCToken') || '',
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

export default function EmailExchangeCallbackPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus]     = React.useState<CallbackStatus>('processing');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [countdown, setCountdown] = React.useState(5);
  const calledRef = React.useRef(false);

  React.useEffect(() => {
    if (status !== 'success') return;
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.close();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  React.useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code  = searchParams.get('code');
    const state = searchParams.get('state');

    // Clean URL so params don't re-trigger on refresh
    setSearchParams({}, { replace: true });

    if (!code || !state) {
      setStatus('error');
      setErrorMsg('Authorization parameters are missing. Please try connecting again.');
      return;
    }

    axios
      .post(
        `${API_BASE}/api/igigcoi/postLoginCallback`,
        { code, state },
        { headers: authHeaders() }
      )
      .then((res) => {
        if (res.data?.success === false) {
          setStatus('error');
          setErrorMsg(res.data?.message || 'Email authorization failed.');
        } else {
          setStatus('success');
        }
      })
      .catch((err) => {
        setStatus('error');
        setErrorMsg(err?.response?.data?.message || 'Failed to complete email authorization.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="loginShell">
      <div className="loginShell__panel">

        {/* Farmers logo */}
        <div className="loginShell__brand">
          <img src={FarmersLogo} alt="Farmers Insurance" className="loginShell__brandLogo" />
        </div>

        {/* Processing */}
        {status === 'processing' && (
          <>
            <div style={{ margin: '8px 0 16px' }}>
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
            </div>
            <div className="loginShell__headline" style={{ fontSize: 20 }}>Connecting your email...</div>
            <p className="loginShell__subtext">
              Please wait while we finalize your Exchange email connection.
            </p>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <div className="emailcheck__statusIcon emailcheck__statusIcon--success" style={{ margin: '4px auto 16px' }}>
              <i className="pi pi-check" />
            </div>
            <div className="loginShell__headline" style={{ fontSize: 20, color: '#15803d' }}>Email Connected!</div>
            <p className="loginShell__subtext">
              Your Microsoft Exchange account has been successfully connected and is ready to use.
            </p>
            <p className="loginShell__subtext" style={{ fontSize: 13, color: '#94a3b8', marginTop: 0 }}>
              This tab will close automatically in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
            <div className="loginShell__actions" style={{ width: '100%' }}>
              <button
                type="button"
                className="loginShell__outlineBtn"
                onClick={() => window.close()}
              >
                <i className="pi pi-times" style={{ marginRight: 8, fontSize: 14 }} />
                Close Tab
              </button>
            </div>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <div className="emailcheck__statusIcon emailcheck__statusIcon--error" style={{ margin: '4px auto 16px' }}>
              <i className="pi pi-times" />
            </div>
            <div className="loginShell__headline" style={{ fontSize: 20, color: '#b91c1c' }}>Authorization Failed</div>
            {errorMsg && (
              <p className="loginShell__subtext" style={{ color: '#dc2626' }}>{errorMsg}</p>
            )}
            <div className="loginShell__actions" style={{ width: '100%' }}>
              <button
                type="button"
                className="loginShell__primaryBtn"
                onClick={() => navigate('/agentdashboard')}
              >
                <i className="pi pi-home" style={{ marginRight: 8, fontSize: 14 }} />
                Back to Dashboard
              </button>
              <button
                type="button"
                className="loginShell__outlineBtn"
                onClick={() => window.close()}
              >
                <i className="pi pi-times" style={{ marginRight: 8, fontSize: 14 }} />
                Close Tab
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
